<template>
  <div class="container">
    <div class="filter-row">
      <!-- Price Valid From -->
      <div class="filter-item">
        <label for="startDatePicker">Valid From</label>
        <input
            id="startDatePicker"
            type="date"
            v-model="priceValidFrom"
            class="form-control"
            :min="todayDate"
            :disabled="loading"
        />
      </div>

      <!-- Select Item -->
      <div class="filter-item">
        <label for="itemSelect">Item</label>
        <select
            id="itemSelect"
            v-model="selectedItem"
            class="form-control"
            :disabled="loading"
        >
          <option value="" disabled>Select Item</option>
          <option v-for="item in itemsList" :key="item.id" :value="item.id">
            {{ item.variant_name }} ({{ item.sap_code }})
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading Items...
    </div>

    <!-- Upload section -->
    <div class="upload-section" v-if="!loading">
      
      <!-- Instructions Column -->
      <div class="instructions-col">
        <h5 class="instructions-title">Pricing Update Instructions</h5>
        <ul class="instructions-list">
          <li><strong>Step 1:</strong> Pick the <strong>Valid From</strong> date accurately.</li>
          <li><strong>Step 2:</strong> Select the correct <strong>Item</strong> from the list.</li>
          <li>
            <strong>Step 3:</strong> Ensure you are ready to upload the Excel sheet in the standard format. <br/>
            <a href="" @click.prevent="downloadTemplate" class="download-link excel-link">
              <i class="fa fa-file-excel-o"></i> Click here to view sample excel sheet
            </a>
          </li>
          <li><strong>Step 4:</strong> The Excel file <strong>must have only one sheet (tab)</strong>.</li>
        </ul>
      </div>

      <!-- Upload Column -->
      <div class="upload-col">
        <div 
          class="upload-box" 
          @dragover.prevent="dragOver" 
          @dragleave.prevent="dragLeave" 
          @drop.prevent="handleDrop"
          :class="{ 'drag-over': isDragging }"
          @click="!uploadedFile && $refs.fileInput.click()"
        >
          <input type="file" ref="fileInput" @change="handleFileUpload" accept=".xlsx, .xls" style="display: none;" />
          <div class="upload-icon">
            <i class="fa fa-cloud-upload fa-3x" v-if="!uploadedFile"></i>
            <i class="fa fa-file-excel-o fa-3x" style="color: #1D6F42;" v-else></i>
          </div>
          <p v-if="!uploadedFile" class="upload-text">Drag and drop your excel file here<br/><span class="or-text">OR</span><br/><span class="browse-btn">Browse File</span></p>
          <div v-else class="file-uploaded-state">
            <p class="file-name upload-text text-success"><strong>{{ uploadedFile.name }}</strong></p>
            <button class="btn btn-sm btn-outline-danger mt-2 remove-btn" @click.stop="removeFile">
              <i class="fa fa-trash"></i> Remove File
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Section -->
    <div v-if="excelData.length > 0" class="table-container mt-4">
      <h5 class="preview-title">Excel Data Preview <small class="text-muted">({{ excelData.length }} rows)</small></h5>
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="(header, index) in tableHeaders" :key="index" :class="['group-header', getColumnClass(header)]">{{ header }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in excelData" :key="rowIndex">
            <td v-for="(header, colIndex) in tableHeaders" :key="colIndex" :class="['currency-cell', getColumnClass(header), {'text-right': header !== 'District'}]">
              {{ formatCurrency(row[header], header) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Submit Section -->
    <div v-if="excelData.length > 0 && selectedItem && priceValidFrom" class="action-bar mt-auto">
      <div class="summary-text">
        Ready to submit prices for <strong>{{ getSelectedItemName }}</strong>
      </div>
      <button class="btn-submit" @click="submitPrices" :disabled="submitting">
        <span v-if="submitting"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
        <span v-else>Save Prices</span>
      </button>
    </div>
  </div>
</template>

<script>
import { getPromotionItemsList, fetchDistrictsList, saveItemPrice } from "@/services/dataService";
import { useToast } from "vue-toastification";
import * as XLSX from "xlsx";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'),
      companyId: localStorage.getItem('company_id'),
      profileId: localStorage.getItem('profile_id'),
      itemsList: [],
      priceValidFrom: "",
      selectedItem: "",
      loading: false,
      submitting: false,
      
      // Upload state
      isDragging: false,
      uploadedFile: null,
      excelData: [],
      tableHeaders: [],
      apiDistrictsList: [],
    };
  },

  computed: {
    todayDate() {
      return this.getCurrentDate();
    },
    getSelectedItemName() {
      const item = this.itemsList.find(i => i.id === this.selectedItem);
      return item ? `${item.variant_name} (${item.sap_code})` : '';
    }
  },

  created() {
    this.priceValidFrom = this.getCurrentDate();
    this.loadItemsList();
    this.loadDistricts();
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  methods: {
    getLevenshteinDistance(a, b) {
      if(a.length === 0) return b.length; 
      if(b.length === 0) return a.length; 
      let matrix = [];
      for(let i = 0; i <= b.length; i++) { matrix[i] = [i]; }
      for(let j = 0; j <= a.length; j++) { matrix[0][j] = j; }
      for(let i = 1; i <= b.length; i++) {
        for(let j = 1; j <= a.length; j++) {
          if(b.charAt(i-1) == a.charAt(j-1)) {
            matrix[i][j] = matrix[i-1][j-1];
          } else {
            matrix[i][j] = Math.min(matrix[i-1][j-1] + 1, Math.min(matrix[i][j-1] + 1, matrix[i-1][j] + 1));
          }
        }
      }
      return matrix[b.length][a.length];
    },

    findDistrictMatchIndex(excelName) {
      if (!excelName || !this.apiDistrictsList) return -1;
      const lowerExcel = excelName.toString().toLowerCase().trim();
      const firstWordExcel = lowerExcel.split(/[\s-]+/)[0];
      
      // 1. Strict exact match pass first (Prevents subset bugs like Kegalle matching Galle)
      for (let i = 0; i < this.apiDistrictsList.length; i++) {
        if ((this.apiDistrictsList[i].name || "").toLowerCase().trim() === lowerExcel) {
          return i;
        }
      }

      // 2. Best fuzzy match pass
      let bestIdx = -1;
      let minDistance = 999;

      for (let i = 0; i < this.apiDistrictsList.length; i++) {
        const apiName = (this.apiDistrictsList[i].name || "").toLowerCase().trim();
        const firstWordApi = apiName.split(/[\s-]+/)[0];

        // Extreme shortcut for prefixes like Trinco == Trincomalee
        if (firstWordExcel.length > 3 && (apiName.startsWith(firstWordExcel) || lowerExcel.startsWith(firstWordApi))) {
          if (0.5 < minDistance) {
            minDistance = 0.5;
            bestIdx = i;
          }
        }

        const distFull = this.getLevenshteinDistance(lowerExcel, apiName);
        const distWord = this.getLevenshteinDistance(firstWordExcel, firstWordApi);
        const distMin = Math.min(distFull, distWord);
        
        if (distMin < minDistance) {
          minDistance = distMin;
          bestIdx = i;
        }
      }

      // Accept if exact substring match yielded 0.5, or max 2 typos (or 3 for long words like Mulathivu)
      if (minDistance <= 2 || (minDistance <= 3 && firstWordExcel.length > 6)) {
        return bestIdx;
      }
      return -1;
    },

    formatCurrency(value, header) {
      if (header === 'District') return value;
      const num = parseFloat(value);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    getColumnClass(header) {
      if (!header) return '';
      const lowerHeader = header.toLowerCase();
      
      if (lowerHeader.includes('distributor')) {
        if (lowerHeader.includes('without vat')) return 'bg-distributor-1';
        if (lowerHeader === 'distributor vat') return 'bg-distributor-2';
        if (lowerHeader.includes('with vat')) return 'bg-distributor-3';
        return 'bg-distributor-1';
      }
      if (lowerHeader.includes('dealer')) {
        if (lowerHeader.includes('without vat')) return 'bg-dealer-1';
        if (lowerHeader === 'dealer vat') return 'bg-dealer-2';
        if (lowerHeader.includes('with vat')) return 'bg-dealer-3';
        return 'bg-dealer-1';
      }
      if (lowerHeader.includes('consumer')) {
        if (lowerHeader.includes('without vat')) return 'bg-consumer-1';
        if (lowerHeader === 'consumer vat') return 'bg-consumer-2';
        if (lowerHeader.includes('with vat')) return 'bg-consumer-3';
        return 'bg-consumer-1';
      }
      return '';
    },

    getCurrentDate() {
      return new Date().toISOString().split('T')[0];
    },

    async loadDistricts() {
      try {
        const response = await fetchDistrictsList(this.token);
        if (response.data && response.data.status === "success") {
          // Preserve exact API order as full objects to retain access to district IDs
          this.apiDistrictsList = response.data.data;
        }
      } catch (error) {
        console.error("Error loading districts:", error);
      }
    },

    async loadItemsList() {
      try {
        this.loading = true;
        const response = await getPromotionItemsList(this.companyId, this.token);

        if (response.data.status === true) {
          this.itemsList = response.data.data
            .filter(item => item.is_parent_item === 1 || item.is_parent_item === "1" || item.is_parent_item === true);

          if (this.itemsList.length > 0) {
            this.selectedItem = this.itemsList[0].id;
          }
        } else {
          this.toast.error("Failed to fetch items list");
        }
      } catch (error) {
        console.error("Error loading items:", error);
        this.toast.error("Error loading data");
      } finally {
        this.loading = false;
      }
    },

    dragOver() {
      this.isDragging = true;
    },

    dragLeave() {
      this.isDragging = false;
    },

    handleDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileUpload(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      const fileExt = file.name.split('.').pop().toLowerCase();
      
      if (!validTypes.includes(file.type) && fileExt !== 'xlsx' && fileExt !== 'xls') {
        this.toast.error("Please upload a valid Excel file (.xlsx or .xls)");
        return;
      }

      this.uploadedFile = file;
      this.readExcel(file);
    },

    readExcel(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          let visibleTabsCount = 0;
          if (workbook.Workbook && workbook.Workbook.Sheets) {
            workbook.Workbook.Sheets.forEach(sheetObj => {
              // Hidden: 0 or undefined means visible. Hidden: 1 or 2 means hidden.
              if (sheetObj.Hidden === 0 || sheetObj.Hidden === undefined) {
                visibleTabsCount++;
              }
            });
          } else {
            visibleTabsCount = workbook.SheetNames.length;
          }

          if (visibleTabsCount > 1) {
            this.toast.error("Not allowed multiple tabs in uploaded excel.");
            this.excelData = [];
            this.tableHeaders = [];
            this.uploadedFile = null;
            if (this.$refs.fileInput) {
              this.$refs.fileInput.value = '';
            }
            return;
          }

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          // range: 2 skips row 1 and 2, treats row 3 as the header (which is index 0 in rawRows)
          let rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", range: 2 });
          let json = [];
          
          if (rawRows.length > 1) {
            let rawHeaders = rawRows[0];
            let dataRows = rawRows.slice(2); // Skip header (row 3) and first data row (row 4)

            let costIdx = -1;
            for(let i = 0; i < rawHeaders.length; i++) {
              if (String(rawHeaders[i] || "").toLowerCase().includes("cost")) {
                costIdx = i;
                break;
              }
            }

            const columnsConfig = [
              { idx: 1, expected: "district", rename: "District" },
              { idx: costIdx, expected: "cost", rename: "Cost" },
              { idx: 3, expected: "distributor", rename: "New Distributor Price without VAT" },
              { idx: 4, expected: "vat", rename: "Distributor VAT" },
              { idx: 5, expected: "distributor", rename: "Distributor Price With VAT" },
              { idx: 7, expected: "dealer", rename: "New Dealer without VAT Price" },
              { idx: 8, expected: "vat", rename: "Dealer VAT" },
              { idx: 9, expected: "dealer", rename: "Dealer Price With VAT" },
              { idx: 11, expected: "consumer", rename: "New Consumer without VAT price" },
              { idx: 12, expected: "vat", rename: "Consumer VAT" },
              { idx: 13, expected: "consumer", rename: "New Consumer price with VAT" }
            ];

            let newTableHeaders = [];
            let mappedCols = [];

            columnsConfig.forEach(config => {
              if (config.idx === -1) {
                newTableHeaders.push(config.rename);
                mappedCols.push(config);
              } else if (config.idx < rawHeaders.length) {
                const actualName = String(rawHeaders[config.idx] || "").toLowerCase();
                if (actualName.includes(config.expected)) {
                  newTableHeaders.push(config.rename);
                  mappedCols.push(config);
                }
              }
            });

            json = dataRows.map(rowArray => {
              let obj = {};
              mappedCols.forEach(config => {
                let val = config.idx === -1 ? 0 : rowArray[config.idx];
                if (config.rename !== "District") {
                  if (val === undefined || val === null || val === "" || String(val).trim() === "-") {
                    val = 0;
                  } else {
                    const num = parseFloat(val);
                    val = isNaN(num) ? 0 : num;
                  }
                } else {
                  val = val !== undefined ? val : "";
                }
                obj[config.rename] = val;
              });
              return obj;
            }).filter(row => row["District"] && row["District"].toString().trim() !== "");

            // Sort json using apiDistrictsList on the 'District' column
            if (newTableHeaders.length > 0 && this.apiDistrictsList && this.apiDistrictsList.length > 0) {
              const districtCol = "District";
              json.sort((a, b) => {
                const valA = a[districtCol];
                const valB = b[districtCol];
                const idxA = this.findDistrictMatchIndex(valA);
                const idxB = this.findDistrictMatchIndex(valB);
                
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return (valA || "").toString().localeCompare((valB || "").toString());
              });
            }

            this.tableHeaders = newTableHeaders;
            this.excelData = json;
            this.toast.success("Excel sheet parsed successfully!");
          } else {
            this.excelData = [];
            this.tableHeaders = [];
            this.toast.warning("The Excel sheet is empty.");
          }
        } catch (error) {
          console.error("Error parsing Excel:", error);
          this.toast.error("Failed to parse the Excel file.");
          this.excelData = [];
          this.tableHeaders = [];
          this.uploadedFile = null;
        }
      };
      reader.readAsArrayBuffer(file);
    },

    removeFile() {
      this.excelData = [];
      this.tableHeaders = [];
      this.uploadedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    downloadTemplate() {
      const link = document.createElement('a');
      link.href = '/assets/docs/SFA SAMPLE PRICE UPLOAD SHEET.XLSX';
      link.download = 'SFA SAMPLE PRICE UPLOAD SHEET.XLSX';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async submitPrices() {
      if (!this.selectedItem) {
        this.toast.warning("Please select an item.");
        return;
      }

      if (this.excelData.length === 0) {
        this.toast.warning("Please upload an Excel file with prices.");
        return;
      }

      this.submitting = true;

      const selectedItemObj = this.itemsList.find(i => i.id === this.selectedItem);

      const itemPrices = this.excelData.map(row => {
        const dName = row["District"];
        const dIdx = this.findDistrictMatchIndex(dName);
        const dObj = dIdx !== -1 ? this.apiDistrictsList[dIdx] : null;
        
        const formatVal = (val) => {
          if (val === undefined || val === null || val === "") return 0.00;
          const num = parseFloat(val);
          return isNaN(num) ? 0.00 : parseFloat(num.toFixed(2));
        };

        return {
          ditrict_id: dObj ? parseInt(dObj.id) : 0,
          cost: formatVal(row["Cost"]),
          distributor_price_without_vat: formatVal(row["New Distributor Price without VAT"]),
          distributor_vat_amount: formatVal(row["Distributor VAT"]),
          distributor_price_with_vat: formatVal(row["Distributor Price With VAT"]),
          dealer_price_without_vat: formatVal(row["New Dealer without VAT Price"]),
          dealer_vat_amount: formatVal(row["Dealer VAT"]),
          dealer_price_with_vat: formatVal(row["Dealer Price With VAT"]),
          consumer_price_without_vat: formatVal(row["New Consumer without VAT price"]),
          consumer_vat_amount: formatVal(row["Consumer VAT"]),
          consumer_price_with_vat: formatVal(row["New Consumer price with VAT"])
        };
      });

      const payload = {
        price_valid_from: this.priceValidFrom,
        created_by: parseInt(this.profileId) || 0,
        item_id: selectedItemObj ? parseInt(selectedItemObj.item_id) : 0,
        variant_id: selectedItemObj && selectedItemObj.variant_id ? parseInt(selectedItemObj.variant_id) : (selectedItemObj ? parseInt(selectedItemObj.id) : null),
        item_type_id: selectedItemObj && selectedItemObj.item_type_id ? parseInt(selectedItemObj.item_type_id) : 0,
        item_prices: itemPrices
      };

      console.log(JSON.stringify(payload, null, 2));

      try {
        const response = await saveItemPrice(payload, this.token);
        
        if (response.data && response.data.status === "success") {
          this.toast.success(response.data.message || "Item prices from Excel submitted successfully!");
          
          // Reset form after successful submission
          this.excelData = [];
          this.tableHeaders = [];
          this.uploadedFile = null;
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
          }
          
          // Redirect to the history page
          this.$router.push('/item-price-history');
        } else {
          this.toast.error(response.data ? response.data.message : "Failed to save prices.");
        }

      } catch (error) {
        console.error("Submission error:", error);
        this.toast.error("An error occurred while saving.");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
html, body {
  background-color: #f0f0f0;
  height: 100%;
  margin: 0;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
  box-sizing: border-box;
}

/* Filters */
.filter-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.filter-item {
  flex: 1;
  max-width: 300px;
}

label {
  font-weight: bold;
  font-size: 13px;
  color: #555;
  display: block;
  margin-bottom: 5px;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

/* Upload Section Layout */
.upload-section {
  display: flex;
  margin-top: 10px;
  gap: 20px;
}

.instructions-col {
  flex: 1;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.instructions-title {
  color: #333;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: bold;
}

.instructions-list {
  padding-left: 20px;
  color: #555;
  font-size: 14px;
  margin: 0;
}

.instructions-list li {
  margin-bottom: 10px;
  line-height: 1.5;
}

.download-link {
  display: inline-block;
  margin-top: 5px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.download-link:hover {
  text-decoration: underline;
}

.excel-link {
  color: #1D6F42 !important;
  font-weight: 600;
}

.excel-link:hover {
  color: #155230 !important;
}

.file-uploaded-state {
  text-align: center;
}

.remove-btn {
  border-radius: 20px;
  padding: 5px 15px;
  transition: all 0.3s;
}

.upload-col {
  flex: 1;
  display: flex;
  align-items: stretch;
}

.upload-box {
  flex: 1;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.upload-box:hover, .upload-box.drag-over {
  border-color: #1D6F42;
  background-color: #f0fff4;
}

.upload-icon {
  margin-bottom: 15px;
  color: #888;
}

.upload-text {
  text-align: center;
  color: #666;
  font-size: 15px;
}

.or-text {
  font-size: 12px;
  color: #999;
  margin: 10px 0;
  display: inline-block;
}

.browse-btn {
  display: inline-block;
  margin-top: 8px;
  padding: 7px 22px;
  background-color: #1D6F42;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 14px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 2px 6px rgba(29, 111, 66, 0.25);
}

.browse-btn:hover {
  background-color: #155230;
  box-shadow: 0 4px 10px rgba(29, 111, 66, 0.35);
}

.file-name {
  margin-top: 10px;
  word-break: break-all;
  text-align: center;
}

.text-success {
  color: #28a745 !important;
}

/* Table Container */
.table-container {
  flex-grow: 1;
  overflow: auto;
  min-height: 300px;
  border: 1px solid #eee;
  border-radius: 6px;
}

.preview-title {
  margin: 15px;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table th, .data-table td {
  padding: 8px 12px;
  border: 1px solid #ddd;
  text-align: left;
  white-space: nowrap;
}

/* Distributor - Green */
.bg-distributor-1 { background-color: #f2fcf5; }
.bg-distributor-2 { background-color: #e6f9ec; }
.bg-distributor-3 { background-color: #d1f2dc; }

.data-table th.bg-distributor-1 { background-color: #e6f9ec; }
.data-table th.bg-distributor-2 { background-color: #d1f2dc; }
.data-table th.bg-distributor-3 { background-color: #bcebc7; }

/* Dealer - Blue */
.bg-dealer-1 { background-color: #f0f7ff; }
.bg-dealer-2 { background-color: #e0efff; }
.bg-dealer-3 { background-color: #cce5ff; }

.data-table th.bg-dealer-1 { background-color: #e0efff; }
.data-table th.bg-dealer-2 { background-color: #cce5ff; }
.data-table th.bg-dealer-3 { background-color: #b3d7ff; }

/* Consumer - Yellow */
.bg-consumer-1 { background-color: #fffdf0; }
.bg-consumer-2 { background-color: #fffad6; }
.bg-consumer-3 { background-color: #fff5b8; }

.data-table th.bg-consumer-1 { background-color: #fffad6; }
.data-table th.bg-consumer-2 { background-color: #fff5b8; }
.data-table th.bg-consumer-3 { background-color: #ffef99; }

.data-table td.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.data-table th {
  background-color: #f8f9fa;
  color: #444;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Action Bar */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-top: 2px solid #eee;
  border-radius: 0 0 8px 8px;
  margin: 20px -20px -20px -20px;
}

.summary-text {
  color: #666;
  font-size: 14px;
}

.btn-submit {
  background-color: #1D6F42;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
}

.btn-submit:hover {
  background-color: #145231;
}

.btn-submit:disabled {
  background-color: #94bca6;
  cursor: not-allowed;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

@media (max-width: 768px) {
  .upload-section {
    flex-direction: column;
  }
}
</style>